import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { IconColor, IconName, IconSize } from '@freelancer/ui/icon';
import { LinkUnderline } from '@freelancer/ui/link';
import { textVariants } from '@freelancer/ui/text';
import { FontColor, FontWeight } from '@freelancer/ui/text';

@Component({
  standalone: false,
  selector: 'app-navigation-menu-nav-item',
  template: `
    <fl-link
      [flTrackingLabel]="trackingLabel"
      [underline]="LinkUnderline.NEVER"
      [link]="url"
      [fragment]="fragment"
      [display]="'block'"
      [newTab]="false"
      (mouseenter)="onHover(true)"
      (click)="onClick(true)"
    >
      <div
        class="Container"
        tabindex="0"
        [class.IsActive]="isActive"
        [class.IsFlex]="isFlex"
        [class.IsLoggedOut]="isLoggedOut"
        [class.DoubleColumn]="doubleColumn"
      >
        @if (!doubleColumn) {
          <fl-icon
            class="IconColor Icon mr-xsmall"
            [name]="iconName"
            [useIconFont]="useIconFont"
            [color]="IconColor.INHERIT"
            [size]="iconSize"
            [sizeTablet]="IconSize.MID"
          ></fl-icon>
        }
        <div class="Container-box">
          <div class="mr-xsmall">
            <div
              [class.SolutionsWrapper]="doubleColumn"
              class="{{ isMobile ? 'mb-none' : 'mb-xxxsmall' }}"
            >
              @if (doubleColumn) {
                <fl-icon
                  class="IconColor mr-xxsmall"
                  [name]="iconName"
                  [useIconFont]="useIconFont"
                  [color]="iconColor"
                  [size]="IconSize.SMALL"
                ></fl-icon>
              }
              <p [class]="titleLinkClasses">
                {{ titleLink }}
              </p>
            </div>

            <p
              [class]="descriptionClasses"
              [class.mobile:hide]="true"
            >
              {{ description }}
            </p>
          </div>
          @if (iconArrow) {
            <fl-icon
              [name]="'ui-chevron-right'"
              [useIconFont]="true"
              [fillIconFont]="true"
              [color]="iconArrowColor"
              [size]="IconSize.MID"
            ></fl-icon>
          }
        </div>
      </div>
    </fl-link>
  `,
  styleUrls: ['./navigation-menu-nav-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationMenuNavItemComponent implements OnInit, OnChanges {
  IconColor = IconColor;
  IconSize = IconSize;
  LinkUnderline = LinkUnderline;

  titleLinkClasses: string;
  descriptionClasses: string;

  @Input() url?: string;
  @Input() fragment?: string;
  @Input() trackingLabel = 'navigationItemLink';
  @Input() iconName: IconName;
  @Input() titleLink: string;
  @Input() description?: string;
  @Input() iconArrow = true;
  @Input() iconArrowColor = IconColor.FOREGROUND;
  @Input() contentId?: string;
  @Input() isActive = false;
  @Input() isFlex = false;
  @Input() useIconFont = true;
  @Input() iconSize = IconSize.SMALL;
  @Input() isMobile = false;
  @Input() descriptionColor = FontColor.FOREGROUND;
  @Input() isLoggedOut = false;

  /* To be use only on the solutions Callout */
  @Input() iconColor: IconColor = IconColor.INHERIT;
  @Input() doubleColumn = false;

  @Output() hover = new EventEmitter<string | undefined>();
  @Output() clicked = new EventEmitter<string | undefined>();

  onHover(isHovered: boolean): void {
    this.hover.emit(isHovered ? this.contentId : undefined);
  }

  ngOnInit(): void {
    this.computeClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('descriptionColor' in changes) {
      this.computeClasses();
    }
  }

  onClick(isHovered: boolean): void {
    this.clicked.emit(isHovered ? this.contentId : undefined);
  }

  private computeClasses(): void {
    this.titleLinkClasses = textVariants({
      color: this.descriptionColor,
      weight: FontWeight.BOLD,
    });

    this.descriptionClasses = textVariants({
      color: this.descriptionColor,
    });
  }
}
